/* eslint-disable no-console */
import { useCallback } from 'react';
import { IconOverview, IconInputs, IconLabel, IconSettings, IconCPU, IconModule, IconData, IconWorkflow } from 'components/Icons';
import { permissionMatchers } from 'utils/scopes/permissionMatchers';
import { useHasAppLevelScopes } from 'modules/Auth/hooks/scopes';
import { useRouter } from 'next/router';
import getConfig from 'next/config';
import { useHasAccessToAppSettings } from 'modules/AppSettings/AppSettings.hooks';
import { isIMVForDemo } from 'modules/InstalledModuleVersions/utils/imv_utils';
import { FixedModuleVersionsSidebarMenu, ModuleVersionsSidebarMenu } from 'modules/InstalledModuleVersions/components/IMVSidebarMenu';
import { MenuGroup, MenuItem, MenuStack } from './components/Menu';

/**
 * Dynamic modules are enabled when the MODULE_MANAGER_HOST variable is defined and has a value.
 */
const { NEXT_PUBLIC_FIXED_MODULES, NEXT_PUBLIC_MODULE_MANAGER_HOST } = getConfig().publicRuntimeConfig as {
  NEXT_PUBLIC_FIXED_MODULES?: unknown;
  NEXT_PUBLIC_MODULE_MANAGER_HOST?: string;
};

function isFixedModule(item: unknown): item is { module_url: string; display_name: string } {
  if (!item || typeof item !== 'object') return false;
  const obj = item as Record<string, unknown>;
  return typeof obj.module_url === 'string' && typeof obj.display_name === 'string';
}

const FIXED_MODULES = (Array.isArray(NEXT_PUBLIC_FIXED_MODULES) ? NEXT_PUBLIC_FIXED_MODULES : []).filter(isFixedModule);
const MODULE_MANAGER_HOST = NEXT_PUBLIC_MODULE_MANAGER_HOST;
const DYNAMIC_MODULES_ENABLED = MODULE_MANAGER_HOST && MODULE_MANAGER_HOST.length > 0;

interface SidebarMenuContentInterface {
  isCollapsed?: boolean;
}

export const SidebarMenuContent: React.FC<SidebarMenuContentInterface> = ({ isCollapsed }) => {
  const {
    appId = '',
    userOrOrgId,
    installedModuleVersionId,
  } = useRouter().query as { appId: string; userOrOrgId: string; installedModuleVersionId?: string };

  const router = useRouter();
  const appHref = appRelativeHref(userOrOrgId, appId);
  const hasAccessToAppSettings = useHasAccessToAppSettings();
  const hasAccessToInput = useHasAppLevelScopes([permissionMatchers.inputs.list]);
  const hasAccessToTask = useHasAppLevelScopes([permissionMatchers.tasks.list]);
  const hasAccessToDataset = useHasAppLevelScopes([permissionMatchers.datasets.list]);
  const showInstalledModules = Boolean(DYNAMIC_MODULES_ENABLED || isIMVForDemo(userOrOrgId));
  const showFixedModules = Boolean(!DYNAMIC_MODULES_ENABLED && FIXED_MODULES.length);
  const renderMenuText = useCallback((text) => (!isCollapsed ? text : ''), [isCollapsed]);

  // Convert router.query to a flat object with only string/number/boolean/undefined values
  const currentQuery: Record<string, string | number | boolean | undefined> = Object.fromEntries(
    Object.entries(router.query).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v]),
  );

  return (
    <MenuStack withDivider={isCollapsed} data-testid="sidebar-menu-content">
      <MenuGroup title={renderMenuText('App Information')}>
        <MenuItem
          isCollapsed={isCollapsed}
          className="sidebar-menu-item"
          href={appHref(undefined, currentQuery)}
          icon={IconOverview}
          title="Overview"
        />
        {hasAccessToAppSettings && (
          <MenuItem
            isCollapsed={isCollapsed}
            className="sidebar-menu-item"
            href={appHref('/settings', currentQuery)}
            icon={IconSettings}
            title="Settings"
          />
        )}
      </MenuGroup>

      <MenuGroup title={renderMenuText('AI Lake')}>
        {hasAccessToInput && (
          <MenuItem
            isCollapsed={isCollapsed}
            className="sidebar-menu-item"
            href={appHref('/inputs', currentQuery)}
            icon={IconInputs}
            title="Inputs"
          />
        )}
        {hasAccessToDataset && (
          <MenuItem
            isCollapsed={isCollapsed}
            className="sidebar-menu-item"
            href={appHref('/datasets', currentQuery)}
            icon={IconData}
            title="Datasets"
          >
            {renderMenuText('Datasets')}
          </MenuItem>
        )}
        <MenuItem isCollapsed={isCollapsed} className="sidebar-menu-item" href={appHref('/models', currentQuery)} icon={IconCPU} title="Models" />
        <MenuItem
          isCollapsed={isCollapsed}
          className="sidebar-menu-item"
          href={appHref('/workflows', currentQuery)}
          icon={IconWorkflow}
          title="Workflows"
        />
        {DYNAMIC_MODULES_ENABLED && (
          <MenuItem
            isCollapsed={isCollapsed}
            className="sidebar-menu-item"
            href={appHref('/modules', currentQuery)}
            icon={IconModule}
            title="Modules"
          >
            {renderMenuText('Modules')}
          </MenuItem>
        )}
        {hasAccessToTask && (
          <MenuItem
            isCollapsed={isCollapsed}
            className="sidebar-menu-item"
            href={appHref('/tasks', currentQuery)}
            icon={IconLabel}
            title="Labeling Tasks"
          >
            {renderMenuText('Labeling Tasks')}
          </MenuItem>
        )}
      </MenuGroup>

      {showInstalledModules ? (
        <ModuleVersionsSidebarMenu
          userOrOrgId={userOrOrgId}
          appId={appId}
          installedModuleVersionId={installedModuleVersionId}
          appHref={appHref}
          isCollapsed={isCollapsed}
        />
      ) : showFixedModules ? (
        <FixedModuleVersionsSidebarMenu fixedModules={FIXED_MODULES} appHref={appHref} isCollapsed={isCollapsed} />
      ) : null}
    </MenuStack>
  );
};

export function appRelativeHref(
  userOrOrgId: string,
  appId: string,
): (path?: string, queryParams?: Record<string, string | number | boolean | undefined>) => string {
  return (path = '', queryParams) => {
    if (!appId) return '';
    const pathname = `/${userOrOrgId}/${appId}${path}`;
    if (queryParams && Object.keys(queryParams).length > 0) {
      const search = new URLSearchParams(queryParams as Record<string, string>).toString();
      return `${pathname}?${search}`;
    }
    return pathname;
  };
}
